// @ts-nocheck
// SuperAdmin editor for High Risk Work Activities (HRWA) — the catalog of
// high-risk construction work items (seeded with the national 18) plus the
// WHS + SWMS section content each one owns and the trades it applies to.
//
// The model is "author once, inject where relevant": a section authored here on
// an activity is folded into a job's Safety Plan (WHS) and into the SWMS of the
// applicable trades whenever that activity is ticked for the job. Editing here
// flows to every job that has the activity ticked, and re-prompts sign-off.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Stack, Button, IconButton, Typography, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, Autocomplete, Divider, Tooltip,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';

import useAuth from '../../hooks/useAuth';
import useSettings from '../../hooks/useSettings';
import { userRoles } from '../../constants/defaultValues';
import axios from '../../utils/axios';
import Page from '../../components/Page';
import PageHeading from '../../components/PageHeading';
import DocumentSheet from '../../components/documents/DocumentSheet';
import SunEditor from '../../components/editor/suneditor';
import {
  useHrwaActivitiesDetailed,
  useCreateHrwaActivity,
  useUpdateHrwaActivity,
  useDeleteHrwaActivity,
  useCreateHrwaSection,
  useUpdateHrwaSection,
  useDeleteHrwaSection,
  useSetHrwaProfessions,
} from '../../api/hrwa';

const RowCard = styled('button')(({ theme }) => ({
  width: '100%',
  textAlign: 'left',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  background: theme.palette.background.paper,
  font: 'inherit',
  color: 'inherit',
  transition: theme.transitions.create(['border-color', 'background-color']),
  '&:hover': { borderColor: theme.palette.primary.main, background: theme.palette.action.hover },
}));

const listStyle = { marginLeft: 'auto', marginRight: 'auto', maxWidth: 820 };

// decode a possibly JSON-stringified section body (SWMS content is stored
// double-encoded on the legacy path; HRWA content is stored raw, so this is a
// no-op there but keeps us safe if content was pasted from a SWMS section).
const decode = (raw) => {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === 'string' ? parsed : raw;
  } catch {
    return raw;
  }
};

export default function Hrwa() {
  const { themeStretch } = useSettings();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const toast = (variant, message) => enqueueSnackbar(message, { variant });

  useEffect(() => {
    if (user && user.id && user.role !== userRoles.admin && user.role !== userRoles.superadmin) {
      navigate('/');
    }
  }, [user]);

  const { data: activities = [], isLoading } = useHrwaActivitiesDetailed();
  const createActivity = useCreateHrwaActivity();
  const updateActivity = useUpdateHrwaActivity();
  const deleteActivity = useDeleteHrwaActivity();
  const createSection = useCreateHrwaSection();
  const updateSection = useUpdateHrwaSection();
  const deleteSection = useDeleteHrwaSection();
  const setProfessions = useSetHrwaProfessions();

  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const selected = useMemo(
    () => activities.find((a) => a.id === selectedId) || null,
    [activities, selectedId]
  );

  // profession list (SWMS trades) for the applicability picker
  const [professions, setProfessions_] = useState([]);
  useEffect(() => {
    axios
      .get('/v1/documents/profession-list?type=swms')
      .then((r) => setProfessions_(r.data?.data || r.data || []))
      .catch(() => {});
  }, []);

  // create-activity dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');

  // activity meta edit (title/description) inside detail
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDesc, setMetaDesc] = useState('');
  useEffect(() => {
    setMetaTitle(selected?.title || '');
    setMetaDesc(selected?.description || '');
  }, [selectedId, selected?.title, selected?.description]);

  // section editor dialog
  const [sectionEditor, setSectionEditor] = useState(null); // { docType, section|null, content, title }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return activities;
    return activities.filter(
      (a) =>
        (a.title || '').toLowerCase().includes(q) ||
        (a.description || '').toLowerCase().includes(q)
    );
  }, [activities, query]);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    try {
      const created = await createActivity.mutateAsync({
        title: newTitle.trim(),
        description: newDesc.trim() || undefined,
      });
      setCreateOpen(false);
      setNewTitle('');
      setNewDesc('');
      setSelectedId(created?.id ?? null);
    } catch (e) {
      toast('warning', e.message || 'Failed to create activity');
    }
  };

  const handleSaveMeta = async () => {
    if (!selected) return;
    try {
      await updateActivity.mutateAsync({
        id: selected.id,
        title: metaTitle.trim(),
        description: metaDesc,
      });
      toast('success', 'Activity updated');
    } catch (e) {
      toast('warning', e.message || 'Failed to update');
    }
  };

  const handleDeleteActivity = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete "${selected.title}"? Jobs will stop showing this activity.`)) return;
    try {
      await deleteActivity.mutateAsync(selected.id);
      setSelectedId(null);
    } catch (e) {
      toast('warning', e.message || 'Failed to delete');
    }
  };

  const handleSetProfessions = async (ids) => {
    if (!selected) return;
    try {
      await setProfessions.mutateAsync({ activityId: selected.id, professionListIds: ids });
    } catch (e) {
      toast('warning', e.message || 'Failed to set trades');
    }
  };

  const openNewSection = (docType) =>
    setSectionEditor({ docType, section: null, content: '', title: '' });
  const openEditSection = (docType, section) =>
    setSectionEditor({
      docType,
      section,
      content: decode(section.content),
      title: section.title || '',
    });

  const handleSaveSection = async () => {
    if (!selected || !sectionEditor) return;
    const { docType, section, content, title } = sectionEditor;
    try {
      if (section?.id) {
        await updateSection.mutateAsync({ sectionId: section.id, content, title });
      } else {
        await createSection.mutateAsync({ activityId: selected.id, docType, content, title });
      }
      setSectionEditor(null);
    } catch (e) {
      toast('warning', e.message || 'Failed to save section');
    }
  };

  const handleDeleteSection = async (section) => {
    try {
      await deleteSection.mutateAsync(section.id);
    } catch (e) {
      toast('warning', e.message || 'Failed to delete section');
    }
  };

  const renderSectionList = (docType, sections) => (
    <Stack spacing={1.5}>
      {(sections || []).map((s) => (
        <Box key={s.id} sx={{ position: 'relative' }}>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}
          >
            <IconButton size="small" onClick={() => openEditSection(docType, s)} aria-label="edit section">
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => handleDeleteSection(s)} aria-label="delete section">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
          <DocumentSheet html={decode(s.content) || '<p style="opacity:.5">(empty section)</p>'} />
        </Box>
      ))}
      <Button
        startIcon={<AddIcon />}
        variant="outlined"
        onClick={() => openNewSection(docType)}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add {docType.toUpperCase()} section
      </Button>
    </Stack>
  );

  return (
    <Page title="WeSafe - High Risk Work Activities">
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <PageHeading
          title="High Risk Work Activities"
          subtitle="The screening questions asked when creating a job. Each activity owns the WHS + SWMS sections that get added to a job's documents when it's ticked."
          action={
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New activity
            </Button>
          }
        />

        <Box sx={listStyle}>
          <TextField
            fullWidth
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search activities"
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
          {isLoading ? (
            <Typography sx={{ color: 'text.secondary', py: 6, textAlign: 'center' }}>
              Loading…
            </Typography>
          ) : filtered.length === 0 ? (
            <Typography sx={{ color: 'text.secondary', py: 6, textAlign: 'center' }}>
              {activities.length === 0 ? 'No activities yet.' : `No activities match “${query}”.`}
            </Typography>
          ) : (
            filtered.map((a) => (
              <RowCard key={a.id} type="button" onClick={() => setSelectedId(a.id)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <WarningAmberIcon sx={{ color: 'warning.main', flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600 }}>{a.title}</Typography>
                    {a.description && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {a.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                  {a.whsSections?.length ? <Chip size="small" label={`WHS ${a.whsSections.length}`} /> : null}
                  {a.swmsSections?.length ? <Chip size="small" label={`SWMS ${a.swmsSections.length}`} /> : null}
                  <ChevronRightIcon sx={{ color: 'text.disabled' }} />
                </Stack>
              </RowCard>
            ))
          )}
        </Box>

        {/* Create activity */}
        <Dialog fullWidth maxWidth="sm" open={createOpen} onClose={() => setCreateOpen(false)}>
          <DialogTitle>New High Risk Work Activity</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Title"
              placeholder="e.g. Asbestos"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              sx={{ mt: 1, mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Description (the screening question)"
              placeholder="Work involving, or likely to involve, the disturbance of asbestos."
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <LoadingButton
              loading={createActivity.isPending}
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!newTitle.trim()}
              onClick={handleCreate}
            >
              Create
            </LoadingButton>
          </DialogActions>
        </Dialog>

        {/* Activity detail */}
        <Dialog fullWidth maxWidth="lg" open={Boolean(selected)} onClose={() => setSelectedId(null)}>
          {selected && (
            <>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningAmberIcon sx={{ color: 'warning.main' }} />
                {selected.title}
              </DialogTitle>
              <DialogContent dividers>
                <Stack spacing={3} sx={{ py: 1 }}>
                  {/* meta */}
                  <Stack spacing={2}>
                    <TextField
                      label="Title"
                      value={metaTitle}
                      onChange={(e) => setMetaTitle(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Description (screening question)"
                      value={metaDesc}
                      onChange={(e) => setMetaDesc(e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                    />
                    <Box>
                      <LoadingButton
                        loading={updateActivity.isPending}
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveMeta}
                      >
                        Save details
                      </LoadingButton>
                    </Box>
                  </Stack>

                  <Divider />

                  {/* applicability */}
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                      Applies to trades (SWMS)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      The SWMS sections below are added only to these trades' SWMS. Leave empty to
                      apply to every trade on the job.
                    </Typography>
                    <Autocomplete
                      multiple
                      options={professions}
                      getOptionLabel={(o) => o.title || ''}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      value={professions.filter((p) => (selected.professionListIds || []).includes(p.id))}
                      onChange={(_e, value) => handleSetProfessions(value.map((v) => v.id))}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="All trades" />
                      )}
                    />
                  </Box>

                  <Divider />

                  {/* WHS sections */}
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                      Safety Plan (WHS) sections
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Added to a job's Safety Plan when this activity is ticked. Supports{' '}
                      {'{{Token}}'} placeholders.
                    </Typography>
                    {renderSectionList('whs', selected.whsSections)}
                  </Box>

                  <Divider />

                  {/* SWMS sections */}
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                      SWMS sections
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Injected into the SWMS of the applicable trades when this activity is ticked.
                    </Typography>
                    {renderSectionList('swms', selected.swmsSections)}
                  </Box>
                </Stack>
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'space-between' }}>
                <Tooltip title="Delete this activity">
                  <Button color="error" startIcon={<DeleteIcon />} onClick={handleDeleteActivity}>
                    Delete
                  </Button>
                </Tooltip>
                <Button onClick={() => setSelectedId(null)}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Section editor */}
        <Dialog
          fullWidth
          maxWidth="lg"
          open={Boolean(sectionEditor)}
          onClose={() => setSectionEditor(null)}
        >
          <DialogTitle>
            {sectionEditor?.section ? 'Edit' : 'New'} {sectionEditor?.docType?.toUpperCase()} section
          </DialogTitle>
          <DialogContent>
            {sectionEditor && (
              <Box sx={{ pt: 1 }}>
                <TextField
                  fullWidth
                  label="Section title (optional)"
                  value={sectionEditor.title}
                  onChange={(e) => setSectionEditor((s) => ({ ...s, title: e.target.value }))}
                  sx={{ mb: 2 }}
                />
                <div className="editor">
                  <SunEditor
                    setContents={sectionEditor.content}
                    onChange={(c) => setSectionEditor((s) => ({ ...s, content: c }))}
                  />
                </div>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSectionEditor(null)}>Cancel</Button>
            <LoadingButton
              loading={createSection.isPending || updateSection.isPending}
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveSection}
            >
              Save
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
